import { useUser } from "@context/UserContext";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  CarbonForm,
  FormRow,
  FormTotal,
  IconButton,
  Stamp,
  Swipe,
  Textarea,
} from "@scribenote/brian-design-system";
import { canSign } from "@users/userClient";
import { formatDate } from "@utils/format-date";
import { ViewRenderer } from "@views/ViewRenderer";
import type { View, ViewItem } from "@views/viewsClient";
import {
  useRemoveViewItem,
  useRequestViewChanges,
  useSignView,
} from "@views/viewsQueries";
import { useState } from "react";

/**
 * Estimate line items live only in `view.items` (BE-2035); the rendered `root`
 * is notes prose. The rows and total are always computed here, whether the
 * estimate is open (editable) or signed (read-only), so both states render as
 * one form: the `root` notes above the line items and total.
 */
export const isEstimate = (view: View): boolean => view.items.length > 0;

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const parseDecimal = (s: string): [number, number] => {
  const [int, frac = ""] = s.trim().split(".");
  return [Number(`${int}${frac}`), frac.length];
};

/** Exact unit_price × quantity in integer cents (half-up); the decimal strings never touch float. */
const lineCents = (item: ViewItem): number | null => {
  if (item.unitPrice == null) return null;
  const [priceMantissa, priceScale] = parseDecimal(item.unitPrice);
  const [qtyMantissa, qtyScale] = parseDecimal(item.quantity);
  const product = priceMantissa * qtyMantissa;
  const shift = priceScale + qtyScale - 2;
  return shift <= 0
    ? product * 10 ** -shift
    : Math.round(product / 10 ** shift);
};

const itemLabel = (item: ViewItem): string =>
  `${item.name} ×${item.quantity}${item.unit ? ` ${item.unit}` : ""}`;

/** Σ line cents; a null-priced (TBD) row counts as 0. */
const estimateCents = (items: ViewItem[]): number =>
  items.reduce((sum, item) => sum + (lineCents(item) ?? 0), 0);

/**
 * The form's status field. A signed estimate wears the approval stamp (the mark
 * a person left on the record); an open one says in a highlighted word that it is
 * still waiting on someone. One mark per state, never both.
 */
const FormStatus = ({ view }: { view: View }) =>
  view.status === "signed" ? (
    <Stamp tone="accent" data-testid={`estimate-stamp-${view.id}`}>
      Approved
    </Stamp>
  ) : (
    <span className="text-xs">
      <Swipe tone="flag" data-testid={`estimate-status-${view.id}`}>
        Awaiting approval
      </Swipe>
    </span>
  );

/**
 * The line items and running total as ruled form rows. `onRemove` adds the
 * per-row Remove button on the open estimate; omitted on the signed doc, whose
 * rows are locked.
 */
const EstimateItems = ({
  items,
  onRemove,
  removing = false,
}: {
  items: ViewItem[];
  onRemove?: (itemId: string) => void;
  removing?: boolean;
}) => (
  <>
    <ul aria-label="Estimate items" className="flex flex-col">
      {items.map((item) => {
        const price = lineCents(item);
        return (
          <FormRow
            key={item.id}
            label={itemLabel(item)}
            value={price == null ? "price TBD" : currency.format(price / 100)}
          >
            {onRemove && items.length > 1 && (
              <IconButton
                size="sm"
                onClick={() => onRemove(item.id)}
                disabled={removing}
                aria-label={`Remove ${item.name}`}
                data-testid={`estimate-remove-${item.id}`}
              >
                <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
              </IconButton>
            )}
          </FormRow>
        );
      })}
    </ul>

    <FormTotal amount={currency.format(estimateCents(items) / 100)} />
  </>
);

/**
 * An estimate view (BE-2035) as the carbon triplicate: the top copy is the form
 * itself (head strip, the `root` notes, the line items ruled off above the
 * total), with the canary and pink carbons peeking out beneath it.
 *
 * Open, it's the actionable surface: per-row Remove, Ask-for-changes, and
 * Approve (which routes through the view sign machinery `useSignView`, so it's
 * offered only to signer roles), carried in a band under the form the way the
 * counter-side controls sit below the paperwork. `readOnly` (a signed estimate)
 * drops the Remove buttons and that band, so the locked document reads the same
 * as the open form minus its controls.
 *
 * `frameless` is a host hint, not a shape: the estimate is its own paper object
 * either way, so it only decides how far off the page the stack is held (a host
 * that is itself the reading surface gets paper resting on paper).
 */
export const EstimateCard = ({
  view,
  readOnly = false,
  frameless = false,
}: {
  view: View;
  readOnly?: boolean;
  frameless?: boolean;
}) => {
  const { currentUser } = useUser();
  const removeItem = useRemoveViewItem();
  const requestChanges = useRequestViewChanges();
  const signView = useSignView("approve the estimate");
  const [note, setNote] = useState("");

  const canApprove = canSign(currentUser);
  const busy =
    signView.isPending || requestChanges.isPending || removeItem.isPending;

  const remove = (itemId: string) =>
    removeItem.mutate({ viewId: view.id, itemId });

  const ask = () =>
    requestChanges.mutate(
      { viewId: view.id, note },
      { onSuccess: () => setNote("") },
    );

  const approve = () => signView.mutate(view.id);

  // A form carries its date. The approval date joins it once someone signs.
  const stamped = [
    `Dated ${formatDate(view.createdAt)}`,
    view.signedAt ? `Approved ${formatDate(view.signedAt)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    // Same test id either way.
    <CarbonForm lifted={!frameless} data-testid={`estimate-card-${view.id}`}>
      <header className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b border-ink px-4 py-3">
        <div className="min-w-0">
          <p className="text-annotation-caps text-annotation">Estimate</p>
          <p className="mt-1 font-mono text-fine-print text-annotation">
            {stamped}
          </p>
        </div>
        <FormStatus view={view} />
      </header>

      {/* The `root` is notes prose, not a mono/annotation label, so this block
          states `font-body` explicitly rather than leaning on the renderer's
          own default. */}
      <div className="border-b border-rule px-4 py-3 font-body">
        <ViewRenderer view={view} frameless />
      </div>

      <EstimateItems
        items={view.items}
        onRemove={readOnly ? undefined : remove}
        removing={removeItem.isPending}
      />

      {!readOnly && (
        <div className="flex flex-col gap-2.5 rounded-b-[calc(var(--radius-paper)-1px)] border-t border-rule bg-recess p-3">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Request changes (optional)"
            minRows={2}
            maxRows={6}
            disabled={busy}
            data-testid="estimate-request-changes"
          />

          <div className="flex flex-wrap gap-2">
            {canApprove && (
              <Button
                variant="accent"
                size="sm"
                busy={signView.isPending}
                disabled={busy}
                data-testid={`estimate-approve-${view.id}`}
                onClick={approve}
              >
                Approve
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              busy={requestChanges.isPending}
              disabled={busy}
              data-testid={`estimate-ask-${view.id}`}
              onClick={ask}
            >
              Ask for changes
            </Button>
          </div>
        </div>
      )}
    </CarbonForm>
  );
};
